import { z } from 'zod';
import {
  LineItem,
  MgmtFeeLineItemInput,
  StandardLineItemInput,
  ZeroDollarLineItemInput
} from 'shared/src/api/line-item-types';
import { ISODateRange } from 'shared/src/date-string-utils/types';
import { ListMetaApi } from 'shared/src/api/common-api-types';

export const PacingBlockV2ApiResponse = z
  .object({
    dates: ISODateRange,
    budget: z.number().meta({ description: 'Budget allocation for this pacing period' })
  })
  .meta({
    id: 'PacingBlockV2ApiResponse',
    description: 'Pacing block with date range and budget'
  });

export type PacingBlockV2ApiResponse = z.infer<typeof PacingBlockV2ApiResponse>;

const RejectedMediaTraderUserIds = {
  media_trader_user_ids: z
    .unknown()
    .refine(value => value === undefined, {
      error: "media_trader_user_ids is computed from the account's Zoho Team and cannot be set"
    })
    .optional()
    .meta({ description: "Rejected; computed from the account's Zoho Team" })
};

export const StandardLineItemApiCreateRequest = StandardLineItemInput.safeExtend(
  RejectedMediaTraderUserIds
).meta({
  id: 'StandardLineItemApiCreateRequest',
  description: 'Standard line item input for API'
});

export type StandardLineItemApiCreateRequest = z.infer<typeof StandardLineItemApiCreateRequest>;

export const StandardLineItemApiUpdateRequest = StandardLineItemInput.partial()
  .safeExtend({ line_item_type: z.literal('standard'), ...RejectedMediaTraderUserIds })
  .meta({
    id: 'StandardLineItemApiUpdateRequest',
    description: 'Standard line item input for API'
  });
export type StandardLineItemApiUpdateRequest = z.infer<typeof StandardLineItemApiUpdateRequest>;

export const MgmtFeeLineItemApiUpdateRequest = MgmtFeeLineItemInput.partial()
  .safeExtend({ line_item_type: z.literal('management_fee'), ...RejectedMediaTraderUserIds })
  .meta({
    id: 'MgmtFeeLineItemApiUpdateRequest',
    description: 'Mgmt Fee line item input for API'
  });
export type MgmtFeeLineItemApiUpdateRequest = z.infer<typeof MgmtFeeLineItemApiUpdateRequest>;

export const ZeroDollarLineItemApiUpdateRequest = ZeroDollarLineItemInput.partial()
  .safeExtend({ line_item_type: z.literal('zero_dollar'), ...RejectedMediaTraderUserIds })
  .meta({
    id: 'ZeroDollarLineItemApiUpdateRequest',
    description: 'Zero Dollar line item input for API'
  });
export type ZeroDollarLineItemApiUpdateRequest = z.infer<typeof ZeroDollarLineItemApiUpdateRequest>;

export const ProposalLineItemApiUpdateRequest = z
  .discriminatedUnion('line_item_type', [
    StandardLineItemApiUpdateRequest,
    MgmtFeeLineItemApiUpdateRequest,
    ZeroDollarLineItemApiUpdateRequest
  ])
  .meta({
    id: 'ProposalLineItemApiUpdateRequest',
    description: 'Discriminated union by type of line item updates for API input'
  });

export type ProposalLineItemApiUpdateRequest = z.infer<typeof ProposalLineItemApiUpdateRequest>;

export const MgmtFeeLineItemApiCreateRequest = MgmtFeeLineItemInput.safeExtend(
  RejectedMediaTraderUserIds
).meta({
  id: 'MgmtFeeLineItemApiCreateRequest',
  description: 'Management fee line item input for API'
});

export type MgmtFeeLineItemApiCreateRequest = z.infer<typeof MgmtFeeLineItemApiCreateRequest>;

export const ZeroDollarLineItemApiCreateRequest = ZeroDollarLineItemInput.safeExtend(
  RejectedMediaTraderUserIds
).meta({
  id: 'ZeroDollarLineItemApiCreateRequest',
  description: 'Zero dollar line item input for API'
});

export type ZeroDollarLineItemApiCreateRequest = z.infer<typeof ZeroDollarLineItemApiCreateRequest>;

export const LineItemApiCreateRequest = z
  .discriminatedUnion('line_item_type', [
    StandardLineItemApiCreateRequest,
    MgmtFeeLineItemApiCreateRequest,
    ZeroDollarLineItemApiCreateRequest
  ])
  .meta({
    id: 'LineItemApiCreateRequest',
    description: 'Discriminated union of line item types for API input'
  });

export type LineItemApiCreateRequest = z.infer<typeof LineItemApiCreateRequest>;

export const ExecutionLineItemApiUpdateRequest = z
  .object({
    planned_unit_cost: z.number().optional(),
    media_platform_ids: z.array(z.number()).optional(),
    ...RejectedMediaTraderUserIds
  })
  .meta({
    id: 'ExecutionLineItemApiUpdateRequest',
    description: 'Partial update for execution line item editable fields'
  });

export type ExecutionLineItemApiUpdateRequest = z.infer<typeof ExecutionLineItemApiUpdateRequest>;

export const LineItemApiResponse = LineItem.extend({
  media_platforms: z
    .array(
      z.object({
        id: z.number().meta({ description: 'Media platform ID' }),
        name: z.string().meta({ description: 'Media platform name' })
      })
    )
    .meta({
      description:
        'Aggregated list of media platforms associated with this line item via media plans'
    })
})
  .extend({
    execution_status: z
      .union([
        z.literal('no_execution'),
        z.literal('execution_without_media_plans'),
        z.literal('execution_with_media_plans')
      ])
      .meta({
        description:
          'Execution state: no_execution (never promoted), execution_without_media_plans (promoted but no media plans), execution_with_media_plans (has associated media plans)'
      })
  })
  .meta({
    id: 'LineItemApiResponse',
    description:
      'Line item with all calculated fields, execution status, and aggregated platform info'
  });

export type LineItemApiResponse = z.infer<typeof LineItemApiResponse>;

export const LineItemApiListResponse = z
  .object({
    data: z.array(LineItemApiResponse),
    meta: ListMetaApi
  })
  .meta({
    id: 'LineItemApiListResponse',
    description: 'Paginated list of line items'
  });

export type LineItemApiListResponse = z.infer<typeof LineItemApiListResponse>;

export const ExecutionLineItemApiResponse = LineItemApiResponse.extend({
  media_trader_user_ids: z.array(z.uuid()).meta({
    description: "Media trader user IDs, computed from the account's Zoho Team and the channel"
  }),
  media_traders: z
    .array(
      z.object({
        id: z.uuid().meta({ description: 'Media trader ID' }),
        name: z.string().meta({ description: 'Media trader name' })
      })
    )
    .meta({
      description: "Media traders computed from the account's Zoho Team and the channel"
    })
}).meta({
  id: 'ExecutionLineItemApiResponse',
  description:
    'Execution line item with all calculated fields, execution status, aggregated platform info, and media traders'
});

export type ExecutionLineItemApiResponse = z.infer<typeof ExecutionLineItemApiResponse>;

export const ExecutionLineItemApiListResponse = z
  .object({
    data: z.array(ExecutionLineItemApiResponse),
    meta: ListMetaApi
  })
  .meta({
    id: 'ExecutionLineItemApiListResponse',
    description: 'Paginated list of execution line items'
  });

export type ExecutionLineItemApiListResponse = z.infer<typeof ExecutionLineItemApiListResponse>;
